/**
 * Aba Andamentos da OS no mesmo desenho da timeline do modal de Veículo/Cliente.
 * Reutiliza editor rico, menções e anexos do padrão já validado.
 *
 * RELEASE: 2026-10-18 v1.9.0 — Props opcionais readOnly, renderHeaderPrefix e emptyLabel para reuso
 *            no Dashboard de O.S. (Alterações Recentes) sem duplicar o leiaute.
 * RELEASE: 2026-10-18 v1.8.0 — Localização do deslocamento/check-in do prestador com "Ver no mapa".
 * RELEASE: 2026-10-10 v1.6.1 — Motivo/observação de eventos de agenda em caixa
 *            destacada (bg-muted/60), mesmo padrão da descrição na timeline do Cliente.
 * RELEASE: 2026-10-09 v1.6.0 — Cards no padrão da timeline do Veículo/Cliente (v2.0.0):
 *            ícone colorido por evento (sem avatar grande), título + "(por:)", selo Sistema,
 *            somente a hora + avatar pequeno à direita, descrição discreta e Ver mais/menos.
 *            Lógica de dados, criação, edição e anexos preservadas integralmente.
 * RELEASE: 2026-10-09 v1.5.0 — Edição de andamento no mesmo padrão visual do modal de Veículo
 *            (AttachmentUploadArea, miniaturas com X e botões compactos).
 * RELEASE: 2026-10-09 v1.4.0 — Cartão compartilhado para notas e eventos automáticos.
 * RELEASE: 2026-10-09 v1.3.0 — Edição de texto/anexos e eventos operacionais na timeline.
 * RELEASE: 2026-10-09 v1.2.0 — Miniaturas e preview interno no padrão da timeline de Tarefas.
 * RELEASE: 2026-10-09 v1.1.0 — Editor rico, menções e anexos privados.
 * RELEASE: 2026-10-09 v1.0.0 — Timeline dedicada e agrupada por data.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Check, Clock, ExternalLink, FileText, Loader2, MapPin, Pencil, Plus, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';
import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { MentionPickerField, type MentionableMember } from '@/components/shared/MentionPickerField';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { SERVICE_ORDER_COMMENT_MAX, type ServiceOrderTimelineItem } from '@/hooks/useServiceOrderDetails';
import { useQueryClient } from '@tanstack/react-query';
import { getServiceOrderAttachmentUrl, uploadServiceOrderAttachment } from '@/hooks/useServiceOrderAttachments';
import { downloadAttachmentFile, resolvePreviewMediaType } from '@/lib/attachment-preview';
import { sanitizeHtml, stripHtml } from '@/lib/sanitize';
import { Badge } from '@/components/ui/badge';
import { groupByDatePreserveOrder } from '@/lib/utils/group-by-date';
import { describeServiceOrderEvent, getServiceOrderEventLocation, getServiceOrderEventNote, getServiceOrderEventPresentation } from './serviceOrderEventPresentation';

const logger = createLogger('ServiceOrderActivityTab');
const errorMessage = (error: unknown) => (error as { message?: string })?.message ?? 'Não foi possível concluir a ação';

interface Props {
  orderId: string;
  comments: ServiceOrderTimelineItem[];
  loading: boolean;
  locked: boolean;
  addComment: { mutateAsync: (input: { content: string; contentHtml?: string; mentionedUserIds?: string[] }) => Promise<string>; isPending: boolean };
  updateComment: { mutateAsync: (input: { commentId: string; content: string; contentHtml?: string; mentionedUserIds?: string[]; removedAttachmentIds?: string[] }) => Promise<{ removed_paths?: string[] }>; isPending: boolean };
  deleteComment: { mutateAsync: (id: string) => Promise<unknown>; isPending: boolean };
  /** RELEASE: 2026-10-16 v1.7.0 - Adaptador opcional para reutilizar a timeline em outros módulos (ex.: Remessas). Padrão = O.S. */
  adapter?: ActivityTimelineAdapter;
  /** v1.9.0: modo somente leitura (sem novo andamento, editar ou excluir) */
  readOnly?: boolean;
  /** v1.9.0: conteúdo extra no início do cabeçalho de cada cartão (ex.: número da O.S.) */
  renderHeaderPrefix?: (item: ServiceOrderTimelineItem) => ReactNode;
  /** v1.9.0: texto do estado vazio */
  emptyLabel?: string;
}

/** Funções de domínio usadas pela timeline (upload, URL do anexo, apresentação dos eventos e cache). */
export interface ActivityTimelineAdapter {
  uploadAttachment: (p: { orderId: string; commentId: string; tenantId: string; file: File }) => Promise<void>;
  getAttachmentUrl: (path: string) => Promise<string>;
  presentEvent: (type?: string) => ReturnType<typeof getServiceOrderEventPresentation>;
  describeEvent: (event: ServiceOrderTimelineItem, omitNote?: boolean) => string;
  getEventNote: (event: ServiceOrderTimelineItem) => { label: string; text: string } | null;
  refreshQueryKey: unknown[];
}

const SERVICE_ORDER_ADAPTER: ActivityTimelineAdapter = {
  uploadAttachment: uploadServiceOrderAttachment,
  getAttachmentUrl: getServiceOrderAttachmentUrl,
  presentEvent: getServiceOrderEventPresentation,
  describeEvent: describeServiceOrderEvent,
  getEventNote: getServiceOrderEventNote,
  refreshQueryKey: ['service-orders'],
};

/** v1.6.0: separador de data alinhado à coluna do ícone (padrão da timeline do Cliente/Veículo) */
function DateSeparator({ label }: { label: string }) {
  return (
    <div className="my-4 flex items-center justify-center pl-9">
      <div className="h-px flex-1 bg-border" />
      <span className="rounded-full bg-muted px-3 py-0.5 text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

export function ServiceOrderActivityTab({ orderId, comments, loading, locked, addComment, updateComment, deleteComment, adapter = SERVICE_ORDER_ADAPTER, readOnly = false, renderHeaderPrefix, emptyLabel }: Props) {
  // v1.9.0: somente leitura equivale a timeline travada (oculta composer e ações)
  if (readOnly) locked = true;
  // v1.7.0: recarrega a timeline após o envio dos anexos (o texto é salvo antes deles)
  const queryClient = useQueryClient();
  const refreshAfterUpload = () => queryClient.invalidateQueries({ queryKey: adapter.refreshQueryKey });
  const { user, currentTenant } = useAuth();
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  const [formOpen, setFormOpen] = useState(false);
  const [content, setContent] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState<MentionableMember[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});
  const [preparingAttachments, setPreparingAttachments] = useState(false);
  const [lightbox, setLightbox] = useState<{ open: boolean; items: MediaItem[]; index: number }>({ open: false, items: [], index: 0 });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editFiles, setEditFiles] = useState<File[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([]);
  const [editMentionIds, setEditMentionIds] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  // v1.5.0: input oculto exclusivo da edição (padrão do modal de Veículo)
  const editFileRef = useRef<HTMLInputElement>(null);

  /** v1.6.0: descrições expandidas (Ver mais / Ver menos) — padrão do Cliente/Veículo */
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const toggleDescription = useCallback((id: string) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Prepara URLs assinadas para o componente compartilhado sem tornar o bucket público. */
  useEffect(() => {
    const attachments = comments.flatMap((comment) => comment.attachments ?? []);
    let active = true;
    if (attachments.length === 0) {
      setAttachmentUrls({});
      setPreparingAttachments(false);
      return () => { active = false; };
    }
    setPreparingAttachments(true);
    Promise.all(attachments.map(async (attachment) => {
      try {
        return [attachment.id, await adapter.getAttachmentUrl(attachment.storage_path)] as const;
      } catch (error) {
        logger.warn('Falha ao preparar anexo da OS', { attachmentId: attachment.id, error: errorMessage(error) });
        return [attachment.id, ''] as const;
      }
    })).then((entries) => {
      if (!active) return;
      setAttachmentUrls(Object.fromEntries(entries));
      setPreparingAttachments(false);
    });
    return () => { active = false; };
  }, [comments]);

  /** Abre uma galeria interna ou baixa formatos que o navegador não visualiza. */
  const openAttachment = async (comment: ServiceOrderTimelineItem, attachmentId: string) => {
    const attachment = comment.attachments?.find((item) => item.id === attachmentId);
    const signedUrl = attachment ? attachmentUrls[attachment.id] : undefined;
    if (!attachment || !signedUrl) {
      toast.error('Não foi possível preparar o arquivo');
      return;
    }
    const mediaType = resolvePreviewMediaType(attachment.file_type, attachment.mime_type);
    if (!mediaType) {
      try { await downloadAttachmentFile(signedUrl, attachment.file_name); }
      catch { toast.error('Erro ao baixar arquivo'); }
      return;
    }
    const previewable = (comment.attachments ?? []).flatMap((item) => {
      const url = attachmentUrls[item.id];
      const type = resolvePreviewMediaType(item.file_type, item.mime_type);
      return url && type ? [{ id: item.id, media: { url, type, title: item.file_name } as MediaItem }] : [];
    });
    const index = previewable.findIndex((item) => item.id === attachment.id);
    setLightbox({ open: true, items: previewable.map((item) => item.media), index: Math.max(0, index) });
  };

  const groups = useMemo(() => {
    const sorted = [...comments].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    return groupByDatePreserveOrder(sorted);
  }, [comments]);

  const submit = async () => {
    const clean = stripHtml(content).trim();
    if (!clean) return;
    try {
      const commentId = await addComment.mutateAsync({ content: clean, contentHtml: sanitizeHtml(content), mentionedUserIds: mentionedUsers.map((member) => member.id) });
      if (files.length && currentTenant?.id) {
        setUploading(true);
        try {
          for (const file of files) await adapter.uploadAttachment({ orderId, commentId, tenantId: currentTenant.id, file });
        } finally { refreshAfterUpload(); }
      }
      setContent('');
      setMentionedUsers([]);
      setFiles([]);
      setFormOpen(false);
    } catch (error) {
      logger.error('Erro ao salvar andamento da OS', error);
      toast.error(errorMessage(error));
    } finally { setUploading(false); }
  };

  const remove = async (id: string) => {
    const confirmed = await confirmDialog({ title: 'Excluir andamento?', description: 'Esta ação não pode ser desfeita.', variant: 'danger', confirmText: 'Excluir' });
    if (!confirmed) return;
    try { await deleteComment.mutateAsync(id); }
    catch (error) { toast.error(errorMessage(error)); }
  };

  /** Abre a edição preservando menções e anexos que já existem. */
  const beginEdit = (comment: ServiceOrderTimelineItem) => {
    setEditingId(comment.id);
    setEditContent(comment.content_html || comment.content || '');
    setEditMentionIds(comment.mentioned_user_ids ?? []);
    setRemovedAttachmentIds([]);
    setEditFiles([]);
  };

  const saveEdit = async (comment: ServiceOrderTimelineItem) => {
    const clean = stripHtml(editContent).trim();
    if (!clean) return;
    try {
      setUploading(editFiles.length > 0);
      const result = await updateComment.mutateAsync({ commentId: comment.id, content: clean, contentHtml: sanitizeHtml(editContent), mentionedUserIds: editMentionIds, removedAttachmentIds });
      if (result.removed_paths?.length) {
        const { error } = await (await import('@/integrations/supabase/client')).supabase.storage.from('service-orders').remove(result.removed_paths);
        if (error) logger.warn('Metadados removidos, mas o arquivo privado não pôde ser excluído', { error: error.message });
      }
      if (currentTenant?.id && editFiles.length) {
        try {
          for (const file of editFiles) await adapter.uploadAttachment({ orderId, commentId: comment.id, tenantId: currentTenant.id, file });
        } finally { refreshAfterUpload(); }
      }
      setEditingId(null); setEditFiles([]); setRemovedAttachmentIds([]);
    } catch (error) { logger.error('Erro ao editar andamento da OS', error); toast.error(errorMessage(error)); }
    finally { setUploading(false); }
  };

  if (loading) {
    // v1.6.0: skeleton no novo formato (ícone redondo à esquerda + card com linhas)
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((item) => (
          <div key={item} className="flex gap-2.5">
            <Skeleton className="h-8 w-6 shrink-0 rounded-full" />
            <div className="flex-1 space-y-2 rounded-lg border p-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-3">
        {!locked && (!formOpen ? (
          <Button type="button" variant="outline" onClick={() => setFormOpen(true)} className="h-auto w-full justify-start gap-3 border-dashed p-3 text-muted-foreground hover:border-primary/40 hover:bg-muted/30 hover:text-foreground">
            <SmartAvatar name={user?.user_metadata?.full_name || user?.email} src={user?.user_metadata?.avatar_url} size="sm" />
            <span className="text-sm font-normal">Adicionar andamento...</span>
          </Button>
        ) : (
          <div className="rounded-lg border bg-card shadow-sm">
            <div className="flex items-center justify-between rounded-t-lg border-b bg-muted/30 px-3 py-2">
              <div className="flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /><span className="text-sm font-medium">Novo Andamento</span></div>
              <div className="flex items-center gap-1.5">
                 <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setFormOpen(false); setContent(''); setFiles([]); setMentionedUsers([]); }} disabled={addComment.isPending || uploading}><X className="mr-1 h-3.5 w-3.5" />Cancelar</Button>
                 <Button size="sm" className="h-7 text-xs" onClick={submit} disabled={!stripHtml(content).trim() || addComment.isPending || uploading}>
                  {addComment.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}Criar
                </Button>
              </div>
            </div>
             <div className="space-y-3 p-3">
               <RichTextEditor content={content} onChange={setContent} placeholder="Descreva o andamento..." minHeight="110px" maxHeight="240px" autoFocus />
               <MentionPickerField value={mentionedUsers} onChange={setMentionedUsers} disabled={addComment.isPending || uploading} />
               <input ref={fileRef} type="file" multiple className="hidden" onChange={(event) => setFiles((current) => [...current, ...Array.from(event.target.files ?? [])].slice(0, 20))} />
               <AttachmentUploadArea onUploadClick={() => fileRef.current?.click()} isUploading={uploading} maxSizeLabel="Máx: 50MB por arquivo" />
               {files.length > 0 && <div className="flex flex-wrap gap-2">{files.map((file, index) => <span key={`${file.name}-${index}`} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs"><FileText className="h-3.5 w-3.5" /><span className="max-w-44 truncate">{file.name}</span><Button type="button" variant="ghost" size="icon" className="h-5 w-5" onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}><X className="h-3 w-3" /></Button></span>)}</div>}
               <p className="text-right text-[10px] text-muted-foreground">{stripHtml(content).length}/{SERVICE_ORDER_COMMENT_MAX}</p>
             </div>
          </div>
        ))}

        {comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50"><Clock className="h-6 w-6 text-muted-foreground" /></div>
            <p className="text-sm font-medium text-muted-foreground">{emptyLabel ?? 'Nenhum andamento registrado'}</p>
            {!readOnly && <p className="mt-1 text-xs text-muted-foreground/70">Adicione notas e atualizações sobre esta ordem de serviço</p>}
          </div>
        ) : (
          <div className="space-y-1">
            {groups.map((group) => (
              <div key={group.dateKey}>
                <DateSeparator label={group.dateLabel} />
                <div className="space-y-3">
                  {group.items.map((comment) => {
                    const isEvent = comment.kind === 'event';
                    // v1.6.0: ícone/cor por tipo de evento — padrão da timeline do Veículo/Cliente
                    const presentation = isEvent
                      ? adapter.presentEvent(comment.event_type)
                      : { Icon: FileText, tone: 'bg-muted text-muted-foreground', badgeTone: '', badgeLabel: '' };
                    const Icon = presentation.Icon;
                    // Título do cabeçalho: eventos usam o rótulo do badge; notas usam "Andamento"
                    const title = isEvent ? presentation.badgeLabel : 'Andamento';
                    const canManage = !isEvent && comment.is_mine && !locked;

                    return (
                      <div key={comment.id} className="group flex gap-2.5">
                        {/* Ícone do tipo de evento (coluna esquerda, sem fundo) */}
                        <div className="flex h-8 w-6 flex-shrink-0 items-center justify-center">
                          <Icon className={cn('h-4 w-4', presentation.tone.split(' ').pop())} />
                        </div>

                        {/* Card do andamento — efeitos premium iguais ao Veículo/Cliente */}
                        <div className="min-w-0 flex-1 rounded-lg border border-border/60 bg-card shadow-sm transition-all duration-200 hover:border-border hover:shadow-md">
                          {/* Cabeçalho: título + "(por:)" + selo à esquerda; ações/hora/avatar à direita */}
                          <div className="flex items-start justify-between gap-2 px-3 py-2">
                            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                              {renderHeaderPrefix?.(comment)}
                              <span className="text-[13px] font-semibold text-foreground">{title}</span>
                              {comment.author_name && (
                                <span className="text-[11px] text-muted-foreground">(por: {comment.author_name})</span>
                              )}
                              {isEvent && (
                                <Badge variant="secondary" className="h-4 px-1.5 py-0 text-[9px]">Sistema</Badge>
                              )}
                              {/* Selo quando o autor é um prestador do portal (author_kind da RPC) */}
                              {comment.author_kind === 'provider' && (
                                <Badge variant="outline" className="h-4 px-1.5 py-0 text-[9px]">Prestador</Badge>
                              )}
                            </div>

                            <div className="flex flex-shrink-0 items-center gap-1">
                              {canManage && (
                                <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:bg-primary/10 hover:text-primary" onClick={() => beginEdit(comment)}>
                                        <Pencil className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">Editar</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" onClick={() => remove(comment.id)} disabled={deleteComment.isPending}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">Excluir</TooltipContent>
                                  </Tooltip>
                                </div>
                              )}

                              {/* Somente a hora — a data fica no separador do dia */}
                              <span className="whitespace-nowrap text-xs text-muted-foreground">
                                {format(new Date(comment.created_at), 'HH:mm', { locale: ptBR })}
                                {comment.updated_at && +new Date(comment.updated_at) > +new Date(comment.created_at) + 1000 && (
                                  <span className="ml-1 text-[10px] italic text-muted-foreground/70">(editado)</span>
                                )}
                              </span>

                              {/* Avatar pequeno do responsável */}
                              {comment.author_name && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <SmartAvatar
                                        src={comment.avatar_url || undefined}
                                        name={comment.author_name}
                                        size="sm"
                                        className="h-6 w-6 border border-background shadow-sm"
                                      />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="text-xs">{comment.author_name}</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </div>

                          {/* Conteúdo */}
                          <div className="px-3 pb-2">
                            {!isEvent && editingId === comment.id ? (
                              /* Modo edição inline (v1.5.0 — padrão do modal de Veículo, preservado) */
                              <div className="space-y-2">
                                <RichTextEditor content={editContent} onChange={setEditContent} placeholder="Editar andamento..." minHeight="80px" maxHeight="250px" fontSize="sm" showToolbar autoFocus />

                                {/* Anexos já existentes — miniaturas com X, iguais às do Veículo */}
                                {(comment.attachments ?? []).filter((attachment) => !removedAttachmentIds.includes(attachment.id)).length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {(comment.attachments ?? []).filter((attachment) => !removedAttachmentIds.includes(attachment.id)).map((attachment) => (
                                      <AttachmentThumbnail
                                        key={attachment.id}
                                        attachment={{
                                          id: attachment.id,
                                          file_name: attachment.file_name,
                                          file_type: attachment.file_type || 'file',
                                          public_url: attachmentUrls[attachment.id] || '',
                                          thumbnail_url: attachment.file_type === 'image' ? attachmentUrls[attachment.id] || null : null,
                                          file_size: attachment.file_size,
                                        }}
                                        onDelete={() => setRemovedAttachmentIds((current) => [...current, attachment.id])}
                                      />
                                    ))}
                                  </div>
                                )}

                                {/* Novos arquivos aguardando envio — miniaturas 14x14 com X */}
                                {editFiles.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {editFiles.map((file, index) => (
                                      <div key={`edit-staged-${index}`} className="group relative">
                                        <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
                                          {file.type.startsWith('image/') ? (
                                            <img src={URL.createObjectURL(file)} alt={file.name} className="h-full w-full object-cover" />
                                          ) : (
                                            <span className="truncate px-1 text-center text-[9px] text-muted-foreground">{file.name.split('.').pop()?.toUpperCase()}</span>
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => setEditFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                                          className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] text-destructive-foreground opacity-0 transition-opacity group-hover:opacity-100"
                                        >
                                          <X className="h-2.5 w-2.5" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <AttachmentUploadArea onUploadClick={() => editFileRef.current?.click()} isUploading={uploading} maxSizeLabel="Máx: 10MB por arquivo" />
                                <input
                                  ref={editFileRef}
                                  type="file"
                                  multiple
                                  className="hidden"
                                  accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                                  onChange={(event) => setEditFiles((current) => [...current, ...Array.from(event.target.files ?? [])].slice(0, 20))}
                                />

                                <div className="flex items-center justify-end gap-1.5">
                                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setEditingId(null)} disabled={updateComment.isPending || uploading}>
                                    <X className="mr-1 h-3.5 w-3.5" />Cancelar
                                  </Button>
                                  <Button type="button" size="sm" className="h-7 text-xs" onClick={() => saveEdit(comment)} disabled={!stripHtml(editContent).trim() || updateComment.isPending || uploading}>
                                    {updateComment.isPending || uploading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />}Salvar
                                  </Button>
                                </div>
                              </div>
                            ) : isEvent ? (
                              /* Evento de sistema: título no cabeçalho; detalhe em linha discreta.
                                 v1.6.1: motivo/observação em caixa destacada (padrão da timeline do Cliente) */
                              (() => {
                                const eventNote = adapter.getEventNote(comment);
                                return (
                                  <div className="space-y-1.5">
                                    {/* v1.6.2: quando a nota vai para a caixa destacada, omite-a do texto principal (evita duplicidade) */}
                                    <p className="text-[11px] text-muted-foreground">{adapter.describeEvent(comment, !!eventNote)}</p>
                                    {eventNote && (
                                      <div className="rounded-md bg-muted/60 px-3 py-2 text-xs text-foreground">
                                        <span className="font-medium">{eventNote.label}:</span> {eventNote.text}
                                      </div>
                                    )}
                                    {/* v1.8.0: localização do deslocamento/check-in do prestador com link do Google Maps */}
                                    {(() => {
                                      const loc = getServiceOrderEventLocation(comment);
                                      if (!loc) return null;
                                      return (
                                        <div className="flex items-center justify-between gap-2 rounded-md bg-muted/60 px-3 py-2">
                                          <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                                            <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
                                            {loc.label}{loc.accuracy !== null && ` · ±${Math.round(loc.accuracy)} m`}
                                          </span>
                                          <a href={loc.url} target="_blank" rel="noopener noreferrer" className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline">
                                            Ver no mapa<ExternalLink className="h-3 w-3" />
                                          </a>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              })()
                            ) : (
                              /* Andamento manual: caixa cinza com Ver mais / Ver menos */
                              <div>
                                <div
                                  className={cn(
                                    'cursor-pointer rounded-md bg-muted/60 px-3 py-2 text-xs text-foreground [&_*]:text-foreground',
                                    !expandedDescriptions.has(comment.id) && 'line-clamp-2',
                                  )}
                                  onClick={() => toggleDescription(comment.id)}
                                >
                                  <div
                                    className="prose prose-xs max-w-none text-xs dark:prose-invert"
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.content_html || comment.content) }}
                                  />
                                </div>
                                <button
                                  type="button"
                                  className="mt-0.5 text-[10px] text-primary/70 hover:text-primary"
                                  onClick={() => toggleDescription(comment.id)}
                                >
                                  {expandedDescriptions.has(comment.id) ? 'Ver menos' : 'Ver mais'}
                                </button>
                              </div>
                            )}

                            {/* Anexos (modo visualização) — miniaturas com preview interno */}
                            {editingId !== comment.id && !!comment.attachments?.length && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {comment.attachments.map((attachment) => {
                                  const signedUrl = attachmentUrls[attachment.id];
                                  if (!signedUrl) return <Skeleton key={attachment.id} className="h-16 w-16 rounded-lg" />;
                                  return (
                                    <AttachmentThumbnail
                                      key={attachment.id}
                                      attachment={{
                                        id: attachment.id,
                                        file_name: attachment.file_name,
                                        file_type: attachment.file_type,
                                        public_url: signedUrl,
                                        thumbnail_url: attachment.file_type === 'image' ? signedUrl : null,
                                        file_size: attachment.file_size,
                                      }}
                                      readOnly
                                      onClick={() => openAttachment(comment, attachment.id)}
                                    />
                                  );
                                })}
                                {preparingAttachments && <span className="sr-only">Preparando anexos</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <ImageLightbox
          open={lightbox.open}
          onClose={() => setLightbox((current) => ({ ...current, open: false }))}
          mediaItems={lightbox.items}
          initialIndex={lightbox.index}
          caption={lightbox.items[lightbox.index]?.title}
        />
        {ConfirmDialogComponent}
      </div>
    </TooltipProvider>
  );
}
